import { useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  ClipboardList,
  ContactRound,
  Inbox,
  Kanban,
  Link2,
  MessagesSquare,
  Network,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '@/features/auth';
import type { Permissions } from '@/shared/api';
import { labelOf, useI18n } from '@/shared/i18n';
import { Logo } from '@/shared/ui/logo';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from '@/shared/ui/shadcn/sidebar';
import { Avatar, AvatarFallback } from '@/shared/ui/shadcn/avatar';

type NavItem = {
  to: string;
  labelKey: keyof ReturnType<typeof useI18n>['t']['nav'];
  icon: LucideIcon;
  permission?: keyof Permissions;
};

type NavGroup = {
  labelKey: keyof ReturnType<typeof useI18n>['t']['nav'];
  items: NavItem[];
};

const navGroups: NavGroup[] = [
  {
    labelKey: 'clients',
    items: [
      { to: '/chats', labelKey: 'chats', icon: Inbox, permission: 'can_view_chats' },
      { to: '/crm', labelKey: 'crm', icon: Kanban, permission: 'can_view_crm' },
      { to: '/contacts', labelKey: 'contacts', icon: ContactRound, permission: 'can_view_crm' },
      { to: '/crm/sources', labelKey: 'sources', icon: Link2, permission: 'can_view_crm' },
    ],
  },
  {
    labelKey: 'improving',
    items: [
      {
        to: '/playground',
        labelKey: 'playground',
        icon: MessagesSquare,
        permission: 'can_use_playground',
      },
      {
        to: '/knowledge',
        labelKey: 'knowledge',
        icon: Network,
        permission: 'can_edit_knowledge_base',
      },
      { to: '/remarks', labelKey: 'remarks', icon: ClipboardList, permission: 'can_view_remarks' },
    ],
  },
  {
    labelKey: 'access',
    items: [{ to: '/users', labelKey: 'users', icon: Users, permission: 'can_manage_users' }],
  },
];

export function AppSidebar() {
  const { user, hasPermission } = useAuth();
  const { t } = useI18n();
  const location = useLocation();
  const { isMobile, setOpenMobile } = useSidebar();
  const closeMobileNav = () => {
    setOpenMobile(false);
  };
  useEffect(() => {
    if (isMobile) setOpenMobile(false);
  }, [isMobile, location.pathname, setOpenMobile]);
  const initials =
    user?.name
      ?.split(/\s+/)
      .map((p) => p[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || 'A';

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader onClick={closeMobileNav}>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              className="group-data-[collapsible=icon]:p-2!"
              render={<NavLink to="/" />}
              tooltip={t.app.name}
              onClick={closeMobileNav}
            >
              <Logo
                variant="mark"
                className="size-8 shrink-0 group-data-[collapsible=icon]:size-4"
              />
              <div className="grid min-w-0 flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                <span className="truncate font-semibold">{t.app.name}</span>
                <span className="truncate text-xs text-muted-foreground">{t.app.admin}</span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent onClick={closeMobileNav}>
        {navGroups.map((group) => {
          const items = group.items.filter(
            (item) => !item.permission || hasPermission(item.permission),
          );
          if (items.length === 0) return null;
          return (
            <SidebarGroup key={group.labelKey}>
              <SidebarGroupLabel>{t.nav[group.labelKey]}</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {items.map((item) => (
                    <SidebarMenuItem key={item.to}>
                      <SidebarMenuButton
                        render={<NavLink to={item.to} />}
                        tooltip={t.nav[item.labelKey]}
                        onClick={closeMobileNav}
                        isActive={
                          location.pathname === item.to ||
                          location.pathname.startsWith(`${item.to}/`)
                        }
                      >
                        <item.icon />
                        <span>{t.nav[item.labelKey]}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          );
        })}
      </SidebarContent>
      <SidebarFooter onClick={closeMobileNav}>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              render={<NavLink to="/profile" />}
              tooltip={user?.name ?? t.nav.profile}
              onClick={closeMobileNav}
              isActive={location.pathname === '/profile'}
            >
              <Avatar className="size-8 shrink-0 rounded-lg">
                <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
              </Avatar>
              <div className="grid min-w-0 flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                <span className="truncate font-semibold">{user?.name}</span>
                <span className="truncate text-xs text-muted-foreground">
                  {labelOf(t.roles, user?.role_key)}
                </span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
